import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Box, Chip, IconButton, Stack, Typography } from '@mui/material';
import { DynamicIcon } from 'src/components';
import { CustomViewFormConditionItem } from 'src/graphql/entities/custom-view';
import { operatorDisplay } from 'src/graphql/entities/custom-view/custom-view-config/operators';
import { ruleValueLabels } from 'src/graphql/entities/custom-view/custom-view-value-format';
import { OptionItem } from './mobile-filter-wizard.helpers';

export const SortableConditionRow = ({
  rule,
  index,
  combinator,
  options,
  onEdit,
  onDelete,
}: {
  rule: CustomViewFormConditionItem;
  index: number;
  combinator?: string;
  options: OptionItem[];
  onEdit: (rule: CustomViewFormConditionItem) => void;
  onDelete: (id?: string) => void;
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable(
    {
      id: rule.id!,
    },
  );

  return (
    <Stack
      ref={setNodeRef}
      gap={0.75}
      sx={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
      }}
    >
      {index > 0 ? (
        <Typography
          variant="caption"
          fontWeight={600}
          sx={{
            alignSelf: 'flex-start',
            marginLeft: '0.25rem',
            letterSpacing: '0.08em',
            color: (theme) => theme.palette.custom.OV.sys.onSurfaceVariant,
          }}
        >
          {combinator === 'or' ? 'OR' : 'AND'}
        </Typography>
      ) : null}
      <Stack
        direction="row"
        alignItems="center"
        gap={0.5}
        onClick={() => onEdit(rule)}
        sx={{
          padding: '0.5rem 0.5rem 0.5rem 0.25rem',
          borderRadius: '0.5rem',
          cursor: 'pointer',
          border: (theme) => `1px solid ${theme.palette.custom.OV.sys.outlineVariant}`,
          backgroundColor: (theme) => theme.palette.custom.OV.sys.surfaceContainerLowest,
        }}
      >
        <Box
          {...attributes}
          {...listeners}
          onClick={(event) => event.stopPropagation()}
          sx={{
            display: 'flex',
            alignItems: 'center',
            cursor: 'grab',
            touchAction: 'none',
            color: (theme) => theme.palette.custom.OV.sys.onSurfaceVariant,
          }}
        >
          <DynamicIcon name="drag_indicator" />
        </Box>
        <Stack
          direction="row"
          alignItems="center"
          flexWrap="wrap"
          gap={0.5}
          flex={1}
          minWidth={0}
        >
          <Typography variant="body2" fontWeight={500}>
            {rule.field?.label}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              padding: '0.125rem 0.375rem',
              borderRadius: '0.375rem',
              color: (theme) => theme.palette.custom.OV.sys.onSurfaceVariant,
              backgroundColor: (theme) => theme.palette.custom.OV.sys.surfaceContainerHigh,
            }}
          >
            {operatorDisplay(rule.operator)}
          </Typography>
          {ruleValueLabels(rule, options).map((label) => (
            <Chip
              key={`${rule.id}-${label}`}
              size="small"
              label={label}
              sx={{
                maxWidth: '100%',
                height: '1.375rem',
                borderRadius: '0.375rem',
                backgroundColor: (theme) => theme.palette.custom.OV.sys.tertiaryContainer,
              }}
            />
          ))}
        </Stack>
        <IconButton
          size="small"
          onClick={(event) => {
            event.stopPropagation();
            onDelete(rule.id);
          }}
        >
          <DynamicIcon name="delete_forever" fontSize="1.1rem" />
        </IconButton>
      </Stack>
    </Stack>
  );
};
